import { Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api, type SessionResponse } from './api.ts';
import { Loading } from './components/Bits.tsx';

import { SignIn } from './pages/SignIn.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { BasicInfo } from './pages/BasicInfo.tsx';
import { SectionPage } from './pages/SectionPage.tsx';
import { Essays } from './pages/Essays.tsx';
import { Financial } from './pages/Financial.tsx';
import { Verification } from './pages/Verification.tsx';
import { NewPassword } from './pages/NewPassword.tsx';
import { Unsubscribe } from './pages/Unsubscribe.tsx';
import { Guidelines } from './pages/Guidelines.tsx';
import { Faq } from './pages/Faq.tsx';
import { HeardAbout } from './pages/HeardAbout.tsx';

import { AdminSignIn } from './pages/admin/AdminSignIn.tsx';
import { AdminHome } from './pages/admin/AdminHome.tsx';
import { AdminApplicant } from './pages/admin/AdminApplicant.tsx';
import { AdminSettings } from './pages/admin/AdminSettings.tsx';
import { AdminCampaigns } from './pages/admin/AdminCampaigns.tsx';

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<SessionResponse>('/api/session'),
  });
}

function ApplicantChrome() {
  const { data } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const signOut = useMutation({
    mutationFn: () => api.del('/api/session'),
    onSuccess: () => {
      queryClient.clear();
      navigate('/');
    },
  });

  return (
    <header className="masthead">
      <h1>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <img src="/logo.gif" alt="" height={28} style={{ display: 'block' }} />
          SEED Foundation Scholarship
        </Link>
      </h1>
      {data?.cycle && <span className="tag">{data.cycle.year}</span>}
      <span className="spacer" />
      <Link to="/guidelines" className="who">Guidelines</Link>
      <Link to="/faq" className="who">FAQ</Link>
      {data?.applicant && (
        <>
          <span className="who">{data.applicant.email}</span>
          <button className="quiet" onClick={() => signOut.mutate()}>Sign out</button>
        </>
      )}
    </header>
  );
}

/** Wraps the applicant area: redirects to sign-in, and forces a temporary password change. */
function RequireApplicant({ children }: { children: React.ReactNode }) {
  const { data, isPending } = useSession();

  if (isPending) return <main className="narrow"><Loading /></main>;
  if (!data?.applicant) return <Navigate to="/" replace />;
  // Someone who signed in with a mailed temporary password must set a real one before they
  // can reach anything else.
  if (data.applicant.mustChangePassword) return <Navigate to="/new-password" replace />;

  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      {/* Admin lives on its own path with its own chrome and its own session. */}
      <Route path="/admin/signin" element={<AdminSignIn />} />
      <Route path="/admin" element={<AdminHome />} />
      <Route path="/admin/applicants/:id" element={<AdminApplicant />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/campaigns" element={<AdminCampaigns />} />

      <Route
        path="*"
        element={
          <>
            <a className="skip-link" href="#main">Skip to content</a>
            <ApplicantChrome />
            <Routes>
              <Route path="/" element={<SignIn />} />
              <Route path="/new-password" element={<NewPassword />} />
              {/* Reached from a link in an email, so it must work signed out. */}
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              {/* Public: an applicant should be able to read the rules before signing up. */}
              <Route path="/guidelines" element={<Guidelines />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/welcome" element={<RequireApplicant><HeardAbout /></RequireApplicant>} />
              <Route path="/apply" element={<RequireApplicant><Dashboard /></RequireApplicant>} />
              <Route path="/apply/basicinfo" element={<RequireApplicant><BasicInfo /></RequireApplicant>} />
              <Route path="/apply/essays" element={<RequireApplicant><Essays /></RequireApplicant>} />
              <Route path="/apply/financial" element={<RequireApplicant><Financial /></RequireApplicant>} />
              <Route path="/apply/verification" element={<RequireApplicant><Verification /></RequireApplicant>} />
              <Route path="/apply/:section" element={<RequireApplicant><SectionPage /></RequireApplicant>} />
              <Route path="*" element={<main className="narrow"><h2>Page not found</h2><p><Link to="/">Go back</Link></p></main>} />
            </Routes>
          </>
        }
      />
    </Routes>
  );
}
